import React, {useEffect, useRef, useState} from 'react';
import { Building, MapPin } from '@phosphor-icons/react';
import {cn} from '../lib/utils';
import {Input} from './ui/input';
import {Label} from './ui/label';
import {useOrgServiceRegionCenter} from '@/hooks/useOrgServiceRegionCenter';
import {applyProximity} from '@/lib/geocodeProximity';
import { addressHasStreetNumber } from '@ruta/shared';
import { reportMapboxApiCall } from '@/lib/integrationUsage';

interface AddressSuggestion {
  id: string;
  place_name: string;
  center: [number, number];
  name?: string;
  address?: string;
  isBusiness?: boolean;
  houseNumber?: string;
}

interface AddressAutocompleteProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string, suggestion?: AddressSuggestion) => void;
  id?: string;
  required?: boolean;
  className?: string;
  allowAnyLocation?: boolean; // New prop to allow businesses, POIs, etc
  onLocationSelect?: (suggestion: AddressSuggestion) => void; // Callback when location is selected
  /** Rank results near the company's service region. Off where no company exists yet (signup). */
  proximityBias?: boolean;
}

export function AddressAutocomplete({
  label,
  placeholder,
  value,
  onChange,
  id,
  className,
  allowAnyLocation = false,
  onLocationSelect,
  proximityBias = true,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Bias address results to the org's service region so a street typed without
  // a city ranks locally instead of returning out-of-state (RUT-3034).
  const proximityCenter = useOrgServiceRegionCenter({ enabled: proximityBias });

  const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

  const searchAddresses = async (query: string) => {
    if (!query.trim() || query.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    if (!MAPBOX_ACCESS_TOKEN) {
      console.warn('Mapbox access token not configured. Address autocomplete disabled.');
      return;
    }

    setIsLoading(true);
    setShowDropdown(true);
    try {
      let url: string;
      
      if (allowAnyLocation) {
        // Use newer Search Box API for better business/POI results
        const params = new URLSearchParams({
          q: query,
          access_token: MAPBOX_ACCESS_TOKEN,
          limit: '8',
          language: 'en',
          types: 'poi,place,address', // Include POIs and places for businesses
        });
        url = `https://api.mapbox.com/search/searchbox/v1/forward?${params.toString()}`;
      } else {
        // Use traditional geocoding API for address-only searches
        const params = new URLSearchParams({
          access_token: MAPBOX_ACCESS_TOKEN,
          types: 'address',
          limit: '8',
          country: 'us'
        });
        applyProximity(params, proximityCenter);
        url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?${params.toString()}`;
      }
      
      const response = await fetch(url);

      if (response.ok) {
        void reportMapboxApiCall();
        const data = await response.json();
        const features = data.features || [];
        
        // Map features to consistent format for both APIs
        const mappedSuggestions = features.map((feature: any) => {
          const isBusiness = allowAnyLocation && feature.properties?.name && feature.properties?.feature_type === 'poi';
          const businessName = feature.properties?.name;
          const address = feature.properties?.full_address || 
                         feature.properties?.place_formatted || 
                         feature.properties?.address;
          
          let displayName = '';
          if (isBusiness) {
            // For businesses, we'll handle the formatting in the UI component
            displayName = businessName;
          } else {
            // For addresses or places, use the standard format
            displayName = feature.properties?.full_address || 
                         feature.properties?.place_formatted || 
                         feature.place_name || 
                         feature.properties?.name ||
                         'Unknown location';
          }
          
          return {
            id: feature.properties?.mapbox_id || feature.id || Math.random().toString(),
            place_name: displayName,
            center: feature.geometry?.coordinates || feature.center || [0, 0],
            name: businessName,
            address: address,
            isBusiness: isBusiness,
            houseNumber: typeof feature.address === 'string' ? feature.address : undefined,
          };
        }).filter((suggestion: AddressSuggestion & { houseNumber?: string }) => {
          if (allowAnyLocation) return true;
          if (suggestion.houseNumber && /^\d/.test(suggestion.houseNumber)) return true;
          return addressHasStreetNumber(suggestion.place_name);
        });
        
        setSuggestions(mappedSuggestions);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    setSelectedIndex(-1);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the API call
    timeoutRef.current = setTimeout(() => {
      searchAddresses(inputValue);
    }, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSuggestions([]);
        setSelectedIndex(-1);
        setShowDropdown(false);
        break;
      case 'Tab':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelect = (suggestion: AddressSuggestion) => {
    onChange(suggestion.place_name, suggestion);
    setSuggestions([]);
    setShowDropdown(false);
    
    // Call the location select callback if provided
    onLocationSelect?.(suggestion);
    
    // Clear any pending search
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  // Picking a suggestion is an offer, not a toll gate: an address typed out in full is a
  // perfectly good answer, so anything clicked outside puts the list away.
  useEffect(() => {
    if (!showDropdown) return;
    const dismiss = (event: PointerEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      setShowDropdown(false);
      setSelectedIndex(-1);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [showDropdown]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <Label htmlFor={id} className="mb-2 block">
          {label}
          {!MAPBOX_ACCESS_TOKEN && (
            <span className="text-xs text-muted-foreground ml-2">(Autocomplete disabled - Mapbox key needed)</span>
          )}
        </Label>
      )}
      
      <Input
        ref={inputRef}
        id={id}
        placeholder={placeholder || (allowAnyLocation ? "Search for any location..." : "Search for an address...")}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        className={className}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-1p-ignore="true"
        data-lpignore="true"
        data-form-type="other"
      />
      
      {/* Search Results Dropdown */}
      {showDropdown && (isLoading || suggestions.length > 0 || (value.length >= 3 && suggestions.length === 0)) && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1.5 bg-white border border-stone-200 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.06),0_10px_25px_-6px_rgba(0,0,0,0.14)] p-[5px] max-h-60 overflow-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <div className="w-4 h-4 border-2 border-stone-300 border-t-primary rounded-full animate-spin" />
              <span className="ml-2 text-sm">Searching addresses...</span>
            </div>
          )}
          {!isLoading && suggestions.length === 0 && value.length >= 3 && (
            <div className="p-4 text-center text-stone-500 text-sm">
              No addresses found for "{value}"
            </div>
          )}
          {!isLoading && suggestions.length > 0 && (
            <div className="py-1">
              {suggestions.map((suggestion, index) => {
                const isSelected = index === selectedIndex;
                const Icon = suggestion.isBusiness ? Building : MapPin;
                return (
                  <button
                    key={suggestion.id}
                    onClick={() => handleSelect(suggestion)}
                    className={cn(
                      "w-full text-left px-2.5 py-2 rounded-[5px] cursor-default text-[13.5px] flex gap-2 items-center",
                      isSelected ? "bg-primary/10 text-stone-900" : "text-stone-700 hover:bg-stone-100"
                    )}
                  >
                    <Icon className="h-4 w-4 text-stone-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      {suggestion.isBusiness ? (
                        <>
                          <div className="font-medium text-stone-900 truncate">
                            {suggestion.name}
                          </div>
                          <div className="text-[11px] text-stone-400 truncate">
                            {suggestion.address}
                          </div>
                        </>
                      ) : (
                        <span className="truncate">{suggestion.place_name}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}